/* Store locator — a pin narrows the retailer list to its address; the same pin, «Show all» or
   Escape widens it again, and the reset is announced. A retailer under the pointer or in focus
   lights its pin. Also: price and stock older than the section's limit are hidden here too, in
   case the page came from Shopify's cache after the nightly sync stopped; and clicks out to a
   retailer are published as customer events (docs/pixels/forhandler-ga4.js sends them to GA4).
   Liquid renders the complete page, so without this file nothing is lost but the filter. */

class MwStoreLocator extends HTMLElement {
  connectedCallback() {
    // Moved in the editor (section reorder): start from the full list again, and set up once.
    if (this.ready) {
      this.show(null);
      return;
    }
    this.ready = true;
    this.pins = [...this.querySelectorAll('button.sloc-pin')];
    this.cards = [...this.querySelectorAll('[data-sloc-card]')];
    this.bar = this.querySelector('[data-sloc-bar]');
    this.text = this.querySelector('[data-sloc-text]');
    this.live = this.querySelector('[data-sloc-live]');
    this.active = null;

    this.addEventListener('click', (event) => this.onClick(event));
    this.addEventListener('keydown', (event) => this.onKey(event));
    this.addEventListener('pointerover', (event) => this.onHover(event));
    this.addEventListener('focusin', (event) => this.onHover(event));
    this.addEventListener('pointerleave', () => this.light(null));
    this.addEventListener('focusout', (event) => {
      if (!this.contains(event.relatedTarget)) this.light(null);
    });

    this.hideStale();
  }

  onClick(event) {
    const pin = event.target.closest('button.sloc-pin');
    if (pin) {
      const id = pin.dataset.slocPin;
      this.show(id === this.active ? null : id);
      this.publish('store_locator_pin', {
        place: pin.dataset.slocPlace,
        count: Number(pin.dataset.slocHere) || undefined,
        active: this.active === id,
      });
      return;
    }
    if (event.target.closest('[data-sloc-reset]')) {
      this.show(null, true);
      return;
    }
    const link = event.target.closest('a[data-sloc-track]');
    if (link) {
      const price = Number.parseFloat(link.dataset.price);
      this.publish('store_locator_click', {
        retailer: link.dataset.retailer,
        target: link.dataset.slocTrack,
        offer: link.dataset.offer || undefined,
        price: Number.isFinite(price) ? price : undefined,
      });
    }
  }

  onKey(event) {
    if (event.key === 'Escape' && this.active) {
      event.stopPropagation();
      this.show(null, true);
    }
  }

  onHover(event) {
    const card = event.target.closest('[data-sloc-card]');
    this.light(card ? card.dataset.slocPin : null);
  }

  light(id) {
    this.pins.forEach((pin) => pin.classList.toggle('is-hot', Boolean(id) && pin.dataset.slocPin === id));
  }

  show(id, returnFocus = false) {
    const previous = this.active;
    this.active = id;
    let pinned = null;
    this.pins.forEach((pin) => {
      const on = pin.dataset.slocPin === id;
      pin.setAttribute('aria-pressed', String(on));
      if (on) pinned = pin;
    });
    this.cards.forEach((card) => {
      card.hidden = Boolean(id) && card.dataset.slocPin !== id;
    });

    const message = pinned ? pinned.dataset.slocStatus : '';
    if (this.text) this.text.textContent = message;
    if (this.bar) this.bar.hidden = !pinned;
    // Widening the list is a change too: say so, instead of going silent.
    if (this.live) this.live.textContent = pinned ? message : previous ? this.dataset.allStatus || '' : '';

    if (!id && returnFocus && previous) {
      this.pins.find((pin) => pin.dataset.slocPin === previous)?.focus();
    }

    // Narrow screens stack the list and the map; bring the result into view.
    if (pinned && this.bar && window.matchMedia('(max-width: 989px)').matches) {
      const smooth = !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.bar.scrollIntoView({ behavior: smooth ? 'smooth' : 'auto', block: 'nearest' });
    }
  }

  /* The server already left stale data out; this covers a cached page served after the
     nightly sync stopped. A retailer link older than the limit keeps its link and loses its price. */
  hideStale() {
    const limit = Number(this.dataset.staleAfter);
    if (!limit) return;
    const now = Date.now() / 1000;
    this.querySelectorAll('a[data-checked]').forEach((cell) => {
      const checked = Number(cell.dataset.checked);
      if (!checked || now - checked > limit) cell.classList.add('is-stale');
    });
    this.querySelectorAll('[data-sloc-checked]').forEach((line) => {
      if (now - Number(line.dataset.slocChecked) > limit) line.hidden = true;
    });
  }

  publish(name, data) {
    try {
      window.Shopify?.analytics?.publish?.(name, data);
    } catch {
      // Measuring must never get in the way of the link.
    }
  }
}

if (!customElements.get('mw-store-locator')) customElements.define('mw-store-locator', MwStoreLocator);
